import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';

export type StatutAbonnement = 'ACTIF' | 'GRACE' | 'LECTURE_SEULE' | 'OFFERT' | 'A_VIE';

export interface PaiementAbonnement {
  id: number; tenant?: string; fournisseur: string; mois: number; montantXof: number; montantDevise?: string; devise?: string;
  statut: 'EN_ATTENTE' | 'PAYE' | 'ECHOUE' | 'EXPIRE' | 'REMBOURSE'; reference: string; transactionFournisseur?: string;
  creeLe: string; payeLe?: string; recuNumero?: string; creePar?: string; motif?: string; redirectUrl?: string;
}

export interface EtatAbonnement {
  tenant: string; statut: StatutAbonnement; finLe?: string; graceJusquau?: string; joursRestants?: number;
  prixMensuelXof: number; montants: Record<string, number>; remises: Record<string, number>; fournisseurs: string[];
  dernierPaiement?: PaiementAbonnement; offertMotif?: string; blocageApplique: boolean;
}

export interface CreationPaiement {
  paiementId: number; montantXof: number; montantDevise?: string; devise?: string; redirectUrl?: string; deepLink?: string; qrCode?: string; reference: string;
}

export interface Tarif {
  prixMensuelXof: number; remise3Mois: number; remise6Mois: number; remise12Mois: number; tauxUsdXof: number; blocageActif: boolean;
  modifiePar?: string; modifieLe?: string;
}

export interface FermeAbonnement {
  tenant: string; nomFerme: string; statut: StatutAbonnement; finLe?: string; graceJusquau?: string; joursRestants?: number;
  offertMotif?: string; blocageTest: boolean; dernierPaiement?: PaiementAbonnement;
}

export interface JournalAbonnement { id: number; tenant: string; le: string; action: string; paiementId?: number; detail?: string; par?: string; niveau: string; }

export const FOURNISSEURS: Record<string, string> = { WAVE: 'Wave', ORANGE_MONEY: 'Orange Money', PAYPAL: 'PayPal', MANUEL: 'Manuel' };
export const STATUTS_ABONNEMENT: Record<StatutAbonnement, { libelle: string; classe: string }> = {
  ACTIF: { libelle: 'Actif', classe: 'text-bg-success' },
  GRACE: { libelle: 'À renouveler', classe: 'text-bg-warning' },
  LECTURE_SEULE: { libelle: 'Lecture seule', classe: 'text-bg-danger' },
  OFFERT: { libelle: 'Offert', classe: 'text-bg-info' },
  A_VIE: { libelle: 'Accès à vie', classe: 'text-bg-primary' },
};

/** Abonnement de la ferme (paiement) et gestion plateforme (super-admin) */
@Injectable({ providedIn: 'root' })
export class AbonnementService {
  private readonly base = `${environment.BASE_URL}/api/abonnement`;
  private readonly plateforme = `${environment.BASE_URL}/api/plateforme/abonnements`;

  constructor(private http: HttpClient) {}

  etat(): Observable<EtatAbonnement> { return this.http.get<EtatAbonnement>(this.base); }

  /** Le client n'envoie que le moyen de paiement et la durée : le montant est calculé par le serveur */
  payer(fournisseur: string, mois: number): Observable<CreationPaiement> {
    return this.http.post<CreationPaiement>(`${this.base}/paiements`, { fournisseur, mois });
  }

  paiement(id: number): Observable<PaiementAbonnement> { return this.http.get<PaiementAbonnement>(`${this.base}/paiements/${id}`); }

  historique(): Observable<PaiementAbonnement[]> { return this.http.get<PaiementAbonnement[]>(`${this.base}/paiements`); }

  recu(id: number): Observable<Blob> { return this.http.get(`${this.base}/paiements/${id}/recu`, { responseType: 'blob' }); }

  // ── Super-admin ──
  moi(): Observable<{ superAdmin: boolean }> { return this.http.get<{ superAdmin: boolean }>(`${this.plateforme}/moi`); }
  fermes(): Observable<FermeAbonnement[]> { return this.http.get<FermeAbonnement[]>(this.plateforme); }
  tarif(): Observable<Tarif> { return this.http.get<Tarif>(`${this.plateforme}/tarif`); }
  modifierTarif(t: Partial<Tarif>): Observable<Tarif> { return this.http.put<Tarif>(`${this.plateforme}/tarif`, t); }
  prolonger(tenant: string, mois: number, jours: number, motif: string): Observable<any> {
    return this.http.post(`${this.plateforme}/${encodeURIComponent(tenant)}/prolonger`, { mois, jours, motif });
  }
  reglage(tenant: string, reglage: 'offert' | 'aVie' | 'blocageTest', valeur: boolean, motif: string): Observable<any> {
    return this.http.post(`${this.plateforme}/${encodeURIComponent(tenant)}/reglage`, { reglage, valeur, motif });
  }
  paiementManuel(tenant: string, mois: number, motif: string, montantXof?: number): Observable<PaiementAbonnement> {
    return this.http.post<PaiementAbonnement>(`${this.plateforme}/${encodeURIComponent(tenant)}/paiement-manuel`, { mois, motif, montantXof });
  }
  rembourser(id: number, motif: string): Observable<PaiementAbonnement> {
    return this.http.post<PaiementAbonnement>(`${this.plateforme}/paiements/${id}/rembourser`, { motif });
  }
  tousPaiements(): Observable<PaiementAbonnement[]> { return this.http.get<PaiementAbonnement[]>(`${this.plateforme}/paiements`); }
  journal(tenant?: string): Observable<JournalAbonnement[]> {
    let params = new HttpParams();
    if (tenant) params = params.set('tenant', tenant);
    return this.http.get<JournalAbonnement[]>(`${this.plateforme}/journal`, { params });
  }
  exporter(): Observable<Blob> { return this.http.get(`${this.plateforme}/export`, { responseType: 'blob' }); }
}
